// loginThrottle — the password sign-in both front doors use: the main app's
// Login and the admin app's (src/admin/AdminLogin.tsx). Moved here from
// modules/shared/Login.tsx when the admin app joined this repo, so the two
// can't drift: the admin door guards the platform's highest-privilege
// accounts, and it is the one that most needs the lockout counter.
//
// Order: `check` the lock → sign in (signInDirect, lock-free) → ALWAYS
// `record` the attempt (success and failure), which drives the per-account
// lock counter and the login audit trail, with a per-IP rate limit on top.
import { reportError } from '@/lib/errorReporter'
import { signInDirect } from '@/lib/authDirect'

const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL  as string
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const THROTTLE_URL  = `${SUPABASE_URL}/functions/v1/login-throttle`

// Human-friendly "until X" string for the lockout message.
function formatLockMessage(lockedUntil: string | undefined): string {
  if (!lockedUntil) return 'Account temporarily locked. Try again later.'
  const until = new Date(lockedUntil)
  const minutesLeft = Math.max(1, Math.ceil((until.getTime() - Date.now()) / 60_000))
  return `Account locked. Try again in ${minutesLeft} minute${minutesLeft === 1 ? '' : 's'}, or ask an admin to unlock you.`
}

// Calls the login-throttle edge function. Per-IP rate-limited before it
// forwards to the underlying RPCs. Returns null on error so we fail open
// for transient infra issues — better than wedging the login page.
async function callThrottle<T>(body: unknown): Promise<T | null> {
  const action = (body as { action?: string } | null)?.action ?? 'unknown'
  try {
    const res = await fetch(THROTTLE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Edge function gateway requires anon key for unauthenticated callers.
        Authorization: `Bearer ${SUPABASE_ANON}`,
        apikey: SUPABASE_ANON,
      },
      body: JSON.stringify(body),
    })
    // Still fail OPEN — a throttle outage must not become a login outage, and
    // login-throttle has no CI deploy. What changed is that it is no longer
    // SILENT.
    //
    // ⚠️ Why this earns its lines. record_login_attempt raised 42703 for every
    // email that EXISTS in auth.users: it ordered by a `created_at` column
    // user_tenants does not have (fixed by mig 20261022000000). The edge
    // function returned 500, this function returned null, and the success path
    // never inspects the result — so the account-lockout counter and the login
    // audit trail were dead for every real account and NOTHING reported it.
    // The migration fixes that instance; this is what makes the next one visible.
    //
    // Only the !res.ok arm reports: the server answered and said no, which is a
    // defect our side. The network arm below deliberately stays quiet — an
    // operator on a dropped packhouse link is not a defect, and reporting it
    // would bury the signal in offline noise.
    //
    // ⚠️ `body` carries the operator's EMAIL and is never reported. What goes
    // out is the action, the status, and the server's own error text — which for
    // the bug above would have read `column ut.created_at does not exist`, i.e.
    // it would have named the defect on day one.
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      void reportError(
        new Error(`login-throttle '${action}' failed: HTTP ${res.status} ${detail.slice(0, 200)}`),
        // `source` is HOW the error was captured (boundary/window/…), not what it
        // was — 'manual' is the right one here; the identity goes in context.
        { context: { component: 'login-throttle', action, status: res.status } },
      )
      return null
    }
    return await res.json() as T
  } catch {
    return null
  }
}

/**
 * Check the lock, sign in, record the attempt. Resolves to the sentence to show
 * the operator, or null on success — the session is then in localStorage and
 * the caller reloads so supabase-js rehydrates from disk with no lock state.
 */
export async function signInThrottled(email: string, password: string): Promise<string | null> {
  // Pre-flight: refuse to even attempt sign-in if the account is locked.
  // Saves a round-trip and gives the user a clear "locked until X" message
  // instead of "Invalid login credentials".
  const lockData = await callThrottle<{ locked: boolean; locked_until?: string }>({
    action: 'check',
    email,
  })
  if (lockData?.locked) return formatLockMessage(lockData.locked_until)

  const { error: authError } = await signInDirect(email, password)

  const attemptData = await callThrottle<{
    locked: boolean
    locked_until?: string
    remaining_attempts?: number
  }>({
    action:     'record',
    email,
    success:    !authError,
    user_agent: navigator.userAgent.slice(0, 200),
  })

  if (!authError) return null
  // If this attempt tipped them over the limit, show the lock message.
  // Otherwise show the auth error + remaining attempts hint.
  if (attemptData?.locked) return formatLockMessage(attemptData.locked_until)
  if (typeof attemptData?.remaining_attempts === 'number') {
    return `${authError} · ${attemptData.remaining_attempts} attempts remaining`
  }
  return authError
}
